import { BufferAttribute, BufferGeometry, Mesh, type Material } from 'three'
import type { MachineCollider, MachineDefinition } from '@core/machine'
import type { PartPalette } from './partsKit'
interface GuideInstance {id:string;collider:MachineCollider;parts:{finish:string;positions:number[];normals:number[];uvs:number[];triangles:number[]}[];auxiliaryColliders?:MachineCollider[]}
interface GuideData {machines:Record<string,GuideInstance[]>}
let data:GuideData|undefined,plastic:GuideData|undefined,lanes:GuideData|undefined
export async function prepareAuthoredGuides(): Promise<void> {
  const [steel,clear,lane]=await Promise.all([import('../../SourceArt/PierGuides/mesh.json'),import('../../SourceArt/PlasticArcGuard/mesh.json'),import('../../SourceArt/LaneGuides/mesh.json')])
  data??=steel.default as unknown as GuideData;plastic??=clear.default as unknown as GuideData;lanes??=lane.default as unknown as GuideData
}
const same = (a: readonly number[], b: readonly number[]) => a.length === b.length
  && a.every((value, i) => Math.abs(value - b[i]!) <= 1e-9)

function matches(collider:MachineCollider,expected:MachineCollider):boolean{
 if(collider.device!==undefined||collider.layer!==expected.layer||collider.material!==expected.material||collider.kind!==expected.kind)return false
 if(collider.kind!=='circle'&&expected.kind!=='circle'&&collider.thickness!==expected.thickness)return false
 switch(collider.kind){
  case 'circle':return expected.kind==='circle'&&same(collider.centre,expected.centre)&&collider.radius===expected.radius
  case 'segment':return expected.kind==='segment'&&same(collider.a,expected.a)&&same(collider.b,expected.b)
  case 'polyline':return expected.kind==='polyline'&&collider.points.length===expected.points.length&&collider.points.every((p,i)=>same(p,expected.points[i]!))
  case 'arc':return expected.kind==='arc'&&same(collider.centre,expected.centre)&&same([collider.radius,collider.angleFrom,collider.angleTo],[expected.radius,expected.angleFrom,expected.angleTo])&&collider.insideOut===expected.insideOut
 }
}
const instancesFor=(definition:MachineDefinition):GuideInstance[]=>[...(data?.machines[definition.machineId]??[]),...(plastic?.machines[definition.machineId]??[]),...(lanes?.machines[definition.machineId]??[])]
function applicable(definition:MachineDefinition,instance:GuideInstance):boolean{
 return (instance.auxiliaryColliders??[]).every(expected=>definition.colliders.some(c=>c.id===expected.id&&matches(c,expected)))
}
function instanceFor(definition: MachineDefinition, collider: MachineCollider) {
  const instance=instancesFor(definition).find(item=>item.id===collider.id)
  if (instance === undefined || collider.device !== undefined) return undefined
  if(!matches(collider,instance.collider)||!applicable(definition,instance))return undefined
  return instance
}
/** End contacts are part of the matched closed guard, rather than extra tall posts. */
export function authoredGuardContact(definition:MachineDefinition,collider:MachineCollider):boolean{
 return instancesFor(definition).some(instance=>{
  if(!instance.auxiliaryColliders?.some(c=>c.id===collider.id&&matches(collider,c)))return false
  const owner=definition.colliders.find(c=>c.id===instance.id)
  return owner!==undefined&&matches(owner,instance.collider)&&applicable(definition,instance)
 })
}

/** One mesh per collider, with the owning cabinet's existing steel material and texture residency. */
export function authoredGuide(definition: MachineDefinition, collider: MachineCollider, palette: PartPalette,
  _illustrated: boolean, steel?: Material): Mesh | undefined {
  if(collider.material!=='plastic'&&Math.abs((collider.kind==='circle'?0:collider.thickness??0)-.0012)>1e-9)return undefined
  const instance = instanceFor(definition, collider)
  if (instance === undefined) return undefined
  const geometry = new BufferGeometry()
  const positions:number[]=[],normals:number[]=[],uvs:number[]=[],triangles:number[]=[],materials:Material[]=[]
  for(const part of instance.parts){
    const offset=positions.length/3,start=triangles.length
    positions.push(...part.positions);normals.push(...part.normals);uvs.push(...part.uvs)
    for(const index of part.triangles)triangles.push(index+offset)
    geometry.addGroup(start,part.triangles.length,materials.length)
    materials.push(part.finish==='opticalGuard'?palette.opticalGuard():palette.guideSteel(steel))
  }
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3))
  geometry.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3))
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2))
  geometry.setIndex(triangles); geometry.computeBoundingBox(); geometry.computeBoundingSphere()
  const clear=instance.parts[0]!.finish==='opticalGuard'
  const mesh=new Mesh(geometry,materials.length===1?materials[0]!:materials)
  mesh.name=`${collider.id}.guide.authored.${clear?'opticalGuard':'HardwareSteel'}`
  return mesh
}
